import { AGORA_SIMULADO } from '../../lib/api';
import { rangeDays } from '../../lib/period';
import { empresaPorId, equipeDaEmpresa, ofertaCiclica } from '../../lib/mock/catalogo';
import { comissaoSimuladaCents } from '../leads/mock';
import type { PeriodRange } from '../../types';
import type { ComercialOverview, ComercialSummary, HandoffItem, HandoffStatus, ReplyVia, Seller } from './types';
import { JANELA_DE_COMISSAO_MS } from './vendaAssistida';

/**
 * Dados simulados do Comercial (RNF19). Deterministicos: hash simples do id da
 * empresa + posicao; nada de Math.random. Volume proporcional a janela.
 *
 * Cada vendedora tem um PERFIL de primeira resposta (uma rapida, uma lenta,
 * uma no meio), para o indicador de SLA de 15 min e o ranking contarem uma
 * historia que da para agir -- e nao tres barras iguais.
 *
 * Venda fechada carrega a comissao CONGELADA com a taxa do catalogo (a da
 * epoca da venda): a administracao mudar a taxa hoje nao reescreve o passado.
 * Credito so dentro de 7 dias do toque (ADR-001); fora disso, sem comissao.
 */

const NOMES = ['Mariana Ferraz', 'João Batista Lemos', 'Aline Souza Prado', 'Ricardo Nunes', 'Beatriz Camargo', 'Felipe Castro', 'Luana Ribeiro', 'Otávio Menezes', 'Carla Mendes', 'Diego Prado', 'Helena Barros', 'Rafael Tavares', 'Sofia Andrade', 'Gustavo Pires', 'Nara Lopes', 'Iara Dias'];
const RESPOSTAS_BOTAO = ['Tenho uma dúvida', 'Não consegui pagar', 'Quero falar com alguém'];
const RESPOSTAS_TEXTO = ['Oi, esse curso serve pra quem está começando do zero?', 'O boleto venceu, consigo gerar outro?', 'Tem desconto se eu pagar à vista no Pix?', 'Vocês parcelam em 12x?', 'Cheguei a preencher tudo e travou no cartão', 'Quanto tempo de acesso eu tenho?'];
const TOQUES = [
  { label: 'WhatsApp T+1h', minutosAposAbandono: 60, funnel: 'carrinho_abandonado' as const },
  { label: 'WhatsApp T+24h', minutosAposAbandono: 60 * 24, funnel: 'carrinho_abandonado' as const },
  { label: 'WhatsApp T+48h', minutosAposAbandono: 60 * 48, funnel: 'carrinho_abandonado' as const },
  { label: 'WhatsApp D+15', minutosAposAbandono: 60 * 24 * 15, funnel: 'expirados' as const },
];

const TOQUE_PADRAO = { label: 'WhatsApp T+1h', minutosAposAbandono: 60, funnel: 'carrinho_abandonado' as const };

/** Teto (min) da primeira resposta por posicao da vendedora: rapida, lenta, no meio. */
const PERFIL_DE_RESPOSTA: readonly number[] = [18, 34, 24];

function hash(texto: string): number {
  let h = 2166136261;
  for (let i = 0; i < texto.length; i += 1) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

function menosMinutos(minutos: number): string {
  return new Date(AGORA_SIMULADO.getTime() - minutos * 60_000).toISOString();
}

function resumo(items: readonly HandoffItem[]): ComercialSummary {
  const fechadas = items.filter((i) => i.status === 'fechou');
  const perdidas = items.filter((i) => i.status === 'perdeu');
  const valor = fechadas.reduce((acc, i) => acc + (i.closedAmountCents ?? 0), 0);
  const respostas = items.map((i) => i.firstResponseMinutes).filter((m): m is number => m !== null);
  const decididas = fechadas.length + perdidas.length;
  return {
    aguardando: items.filter((i) => i.status === 'aguardando').length,
    emAtendimento: items.filter((i) => i.status === 'em_atendimento').length,
    fechadas: fechadas.length,
    perdidas: perdidas.length,
    valorFechadoCents: valor,
    comissaoCents: fechadas.reduce((acc, i) => acc + (i.commissionState === 'creditada' ? i.commissionCents ?? 0 : 0), 0),
    tempoMedioRespostaMinutos: respostas.length === 0 ? null : Math.round(respostas.reduce((a, b) => a + b, 0) / respostas.length),
    taxaFechamentoBps: decididas === 0 ? 0 : Math.round((fechadas.length * 10_000) / decididas),
  };
}

export function simularComercial(companyId: string, range: PeriodRange): ComercialOverview {
  const empresa = empresaPorId(companyId);
  const feeBps = empresa?.feeBps ?? 1_500;
  const dias = rangeDays(range) ?? 30;
  const base = hash(companyId);
  const equipe = equipeDaEmpresa(companyId);
  // Vendedoras: a equipe da empresa menos o dono; sem equipe, duas ficticias.
  const comerciais = equipe.filter((m) => m.role === 'comercial');
  const vendedorasBase = comerciais.length > 0 ? comerciais : equipe.filter((m) => m.role !== 'owner');
  const nomesVendedoras = vendedorasBase.length > 0 ? vendedorasBase.map((m) => ({ id: m.id, name: m.name })) : [
    { id: `SIMULADO-vendedora-${companyId}-1`, name: 'Vendedora 1' },
    { id: `SIMULADO-vendedora-${companyId}-2`, name: 'Vendedora 2' },
  ];

  const quantidade = Math.max(4, Math.min(60, Math.round((dias * 22 * ((base % 60) + 40)) / (30 * 100))));
  const items: HandoffItem[] = [];
  for (let i = 0; i < quantidade; i += 1) {
    const h = hash(`${companyId}:${i}`);
    const oferta = ofertaCiclica(companyId, i);
    const toque = TOQUES[(h >>> 3) % TOQUES.length] ?? TOQUE_PADRAO;
    const via: ReplyVia = (h & 1) === 0 ? 'botao' : 'digitando';
    const repliedMinutesAgo = 30 + ((h >>> 5) % (dias * 24 * 60));
    const statusSorteio = (h >>> 9) % 10;
    const status: HandoffStatus = repliedMinutesAgo < 120 ? 'aguardando' : statusSorteio < 1 ? 'aguardando' : statusSorteio < 4 ? 'em_atendimento' : statusSorteio < 8 ? 'fechou' : 'perdeu';
    const posicao = (h >>> 13) % nomesVendedoras.length;
    const seller = status === 'aguardando' ? null : nomesVendedoras[posicao] ?? null;
    const teto = PERFIL_DE_RESPOSTA[posicao % PERFIL_DE_RESPOSTA.length] ?? 24;
    const firstResponse = status === 'aguardando' ? null : 1 + ((h >>> 15) % teto);
    const closed = status === 'fechou';
    const closedVia: HandoffItem['closedVia'] = closed ? (((h >>> 17) % 3) === 0 ? 'manual' : 'webhook') : null;
    const nome = NOMES[(h >>> 2) % NOMES.length] ?? null;
    const touchMinutesAgo = repliedMinutesAgo + 5 + ((h >>> 19) % 40);
    const closedMinutesAgo = closed ? Math.max(1, repliedMinutesAgo - (firstResponse ?? 10) - ((h >>> 23) % 600)) : null;
    const dentroDaJanela = closedMinutesAgo !== null && (touchMinutesAgo - closedMinutesAgo) * 60_000 <= JANELA_DE_COMISSAO_MS;
    items.push({
      id: `SIMULADO-comercial-${companyId}-${i}`,
      companyId,
      leadId: `SIMULADO-lead-${String((i % 6) + 1).padStart(3, '0')}`,
      leadName: nome,
      phoneE164: `+5511${String(900000000 + ((h >>> 1) % 99999999)).padStart(9, '0')}`,
      offerName: oferta.name,
      amountCents: oferta.priceCents,
      funnel: toque.funnel,
      touchLabel: toque.label,
      touchAt: menosMinutos(touchMinutesAgo),
      repliedAt: menosMinutos(repliedMinutesAgo),
      via,
      replyText: via === 'botao' ? (RESPOSTAS_BOTAO[(h >>> 21) % RESPOSTAS_BOTAO.length] ?? 'Tenho uma dúvida') : (RESPOSTAS_TEXTO[(h >>> 21) % RESPOSTAS_TEXTO.length] ?? 'Oi, tenho uma dúvida'),
      sellerId: seller?.id ?? null,
      status,
      firstResponseMinutes: firstResponse,
      closedAt: closedMinutesAgo === null ? null : menosMinutos(closedMinutesAgo),
      closedAmountCents: closed ? oferta.priceCents : null,
      orderRef: closed ? (closedVia === 'manual' ? `pix-${(h >>> 4) % 90000}` : `${oferta.provider}-${(h >>> 6) % 900000}`) : null,
      closedVia,
      commissionState: closed ? (dentroDaJanela ? 'creditada' : 'sem_comissao') : null,
      commissionCents: closed && dentroDaJanela ? comissaoSimuladaCents(oferta.priceCents, feeBps) : null,
    });
  }
  items.sort((a, b) => (a.status === 'aguardando' ? 0 : 1) - (b.status === 'aguardando' ? 0 : 1) || (a.repliedAt < b.repliedAt ? 1 : -1));

  const sellers: Seller[] = nomesVendedoras.map((v) => ({
    id: v.id,
    name: v.name,
    active: true,
    openCount: items.filter((i) => i.sellerId === v.id && (i.status === 'aguardando' || i.status === 'em_atendimento')).length,
  }));

  const atual = resumo(items);
  // Periodo anterior: mesma formula com semente deslocada e leve variacao.
  const anteriorItems = items.map((i, idx): HandoffItem => {
    const mantem = hash(`${companyId}:prev:${idx}`) % 10 < 6;
    return mantem ? i : { ...i, status: 'perdeu', commissionState: null, commissionCents: null, closedAmountCents: null };
  });
  const previous = resumo(anteriorItems.slice(0, Math.max(3, Math.round(items.length * 0.85))));

  return {
    companyId,
    whatsappNumberE164: `+5511${String(980000000 + (base % 9999999)).padStart(9, '0')}`,
    sellers,
    items,
    summary: atual,
    previous,
    periodStart: range.from ?? menosMinutos(dias * 24 * 60),
    periodEnd: range.to ?? AGORA_SIMULADO.toISOString(),
  };
}
